import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/components/feature/AuthProvider";
import { useClub } from "@/hooks/useClub";
import { assetUrl } from "@/lib/api";

interface NavItem {
  label: string;
  to: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: "메인", to: "/" },
  { label: "클럽소개", to: "/about" },
  { label: "조직도", to: "/org" },
  { label: "규정", to: "/rules" },
  { label: "명함", to: "/cards" },
  { label: "회원", to: "/members" },
  { label: "민들레 비즈맵", to: "/bizmap" },
  { label: "직업홍보", to: "/promo" },
  { label: "자유글", to: "/free" },
  { label: "내가쏜다", to: "/treat" },
  { label: "가위바위보", to: "/rps" },
  { label: "줄다리기", to: "/tug" },
  { label: "게임", to: "/games" },
  { label: "걸음 랭킹", to: "/steps" },
  { label: "투표·설문", to: "/vote" },
  { label: "일정", to: "/calendar" },
  { label: "사다리게임", to: "/ladder" },
  { label: "활동갤러리", to: "/gallery" },
  { label: "공지사항", to: "/notices" },
  { label: "문의하기", to: "/contact" },
];

export default function Header() {
  const { member, openLogin, logout } = useAuth();
  const { club } = useClub();
  const location = useLocation();
  const [userOpen, setUserOpen] = useState(false);

  // 관리자 «로고·배너» 설정값. 아직 club 을 못 받았을 때만 기본 문구로 잠깐 보여준다.
  // (로딩 후에는 서버가 준 값을 그대로 쓴다 — 관리자에서 지우면 화면에서도 사라진다)
  const brand = club?.brand;
  const logo = assetUrl(brand?.logo);
  const line1 = brand?.line1 ?? (club ? "" : "대전민들레로타리클럽");
  const line2 = brand?.line2 ?? (club ? "" : "국제로타리 3680지구");

  const isActive = (item: NavItem) => {
    if (item.to === "/") return location.pathname === "/";
    return location.pathname.startsWith(item.to);
  };

  const linkClass = (active: boolean) =>
    `whitespace-nowrap px-2.5 py-2 rounded-md text-[13px] transition-colors ${
      active
        ? "text-primary-700 font-semibold"
        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
    }`;

  const userLabel = member?.name ? `${member.name}님` : "회원";

  // sections.step 이 꺼져 있으면 «걸음 랭킹» 메뉴를 아예 숨긴다.
  const stepOn = !club?.sections || Number(club.sections.step ?? 1) !== 0;
  const navItems = stepOn
    ? NAV_ITEMS
    : NAV_ITEMS.filter((item) => item.to !== "/steps");

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-3">
          {/* 로고 */}
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            {logo ? (
              <img
                src={logo}
                alt={line1 || "클럽 로고"}
                className="h-11 w-11 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span className="w-11 h-11 flex items-center justify-center rounded-full bg-secondary-500 text-primary-900 shrink-0">
                <i className="ri-seedling-fill text-lg" aria-hidden="true"></i>
              </span>
            )}
            {(line1 || line2) && (
              <span className="flex flex-col leading-tight">
                {line1 && (
                  <span className="font-heading font-bold text-foreground-950 text-[16px] whitespace-nowrap">
                    {line1}
                  </span>
                )}
                {line2 && (
                  <span className="text-[12px] text-foreground-500 whitespace-nowrap">
                    {line2}
                  </span>
                )}
              </span>
            )}
          </Link>

          {/* 데스크톱 메뉴 */}
          <nav className="hidden xl:flex items-center gap-0.5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={linkClass(isActive(item))}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* 로그인 / 사용자 */}
          <div className="relative shrink-0">
            {member ? (
              <>
                <button
                  type="button"
                  onClick={() => setUserOpen((v) => !v)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200 bg-background-50 py-1.5 pl-1.5 pr-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors"
                  aria-haspopup="menu"
                  aria-expanded={userOpen}
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs">
                    {(member.name || "회").slice(0, 1)}
                  </span>
                  {userLabel}
                  <i className="ri-arrow-down-s-line" aria-hidden="true"></i>
                </button>
                {userOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserOpen(false)}
                      aria-hidden="true"
                    ></div>
                    <div
                      role="menu"
                      className="absolute right-0 top-full z-20 mt-1 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1.5 shadow-lg"
                    >
                      <Link
                        to="/my/profile"
                        role="menuitem"
                        onClick={() => setUserOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100"
                      >
                        <i className="ri-id-card-line" aria-hidden="true"></i>
                        내 명함 수정
                      </Link>
                      <Link
                        to="/free"
                        role="menuitem"
                        onClick={() => setUserOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100"
                      >
                        <i className="ri-file-text-line" aria-hidden="true"></i>
                        내 글
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setUserOpen(false);
                          logout();
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100"
                      >
                        <i className="ri-logout-box-r-line" aria-hidden="true"></i>
                        로그아웃
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={openLogin}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-lock-line mr-1.5" aria-hidden="true"></i>
                로그인
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}